import { AGENT_URL } from '../../config/env'
import { apiErrorFrom } from '@/lib/api'
import { isNetworkError } from '@/lib/fetchError'
import type {
  ChatAttachmentUpload,
  UploadFileResponse,
  UploadedFilesResponse,
} from './types'

/** Mirrors MAX_FILE_SIZE_BYTES in hoku/server/handlers/data_files.py. */
export const MAX_UPLOAD_BYTES = 30 * 1024 * 1024

export async function getUploadedFiles(
  token: string,
  params: { limit?: number },
  cursor?: string
): Promise<UploadedFilesResponse> {
  const search = new URLSearchParams()
  if (params.limit) {
    search.set('limit', params.limit.toString())
  }
  if (cursor) {
    search.set('cursor', cursor)
  }

  const response = await fetch(`${AGENT_URL}/data/files?${search.toString()}`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to fetch uploaded files: ${response.status}`
    )
  }

  return response.json()
}

async function postFileForm<T>(
  path: string,
  token: string,
  file: File,
  purpose?: string
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)
  if (purpose) formData.append('purpose', purpose)

  const response = await fetch(`${AGENT_URL}${path}`, {
    method: 'POST',
    headers: {
      Authorization: token,
    },
    body: formData,
  })

  if (!response.ok) throw await apiErrorFrom(response, 'Failed to upload file')

  return response.json()
}

export async function uploadFile(
  token: string,
  file: File,
  // What the file is for, e.g. 'org_logo'. The server decides which bucket
  // serves that purpose; omitted, the file goes to the ordinary one.
  purpose?: string
): Promise<UploadFileResponse> {
  return postFileForm<UploadFileResponse>('/data/files', token, file, purpose)
}

/**
 * Upload one image or PDF for a David chat message. The bytes land in the
 * private chat-attachments bucket; only the returned reference is sent with
 * the message.
 */
export async function uploadChatAttachment(
  token: string,
  file: File
): Promise<ChatAttachmentUpload> {
  const response = await postFileForm<{ file: ChatAttachmentUpload }>(
    '/chat/attachments',
    token,
    file
  )
  return response.file
}

/**
 * Probes whether the upload backend is reachable by issuing a known-good GET.
 * Returns true when the request reaches the server (even with an HTTP error),
 * and false only when it fails at the network layer (never sent). Used to tell
 * "server unreachable" apart from "this upload was blocked before sending".
 */
export async function isUploadBackendReachable(
  token: string
): Promise<boolean> {
  try {
    await getUploadedFiles(token, { limit: 1 })
    return true
  } catch (error) {
    return !isNetworkError(error)
  }
}
